import {createSelector} from '@reduxjs/toolkit';
import isEqual from 'react-fast-compare';
import {Forecast, Station} from '../../utils';
import {getFavoriteLocations} from '../location/selectors';
import {getSortedVisibleStationList} from '../weather/selectors';
import {CommonRootState} from '../types';

/**
 * Everywhere the forecast preference is applied, paired with the model
 * **server names** that reach each place.
 *
 * The forecast resolver runs per *point*, and the app stores the models that
 * reach each point in two places, keyed differently:
 *
 * - `weather.forecastModels`  keyed by generic station id (number)
 * - `location.forecastModels` keyed by saved-location hash (string)
 *
 * Both are read here, and together they are the whole set. `netatmoWeather`
 * used to carry a third `forecastModels` map that looked like a source and was
 * written by nothing and read by nothing; it was removed in FE #198. Netatmo
 * weather screens go through `withNetatmoGenericStation`, which calls
 * `useStationForecast` and so reads `weather.forecastModels` like every other
 * station — so two stores is the whole answer, not an omission to fix.
 *
 * The first version of this read only the location store. Most accounts have
 * weather stations and no saved *locations*, so the list came back empty — and
 * an empty list makes `computePinCoverage` report every pin as reaching
 * nothing, which the screen rendered as "Never used — it reaches none of your
 * stations" for a pin that was in fact serving the user's station. Caught on a
 * real device; no unit test could have, because they all supplied the station
 * list directly.
 *
 * Hidden stations are excluded: they are not shown anywhere else either, so
 * counting them would inflate "used at 2 of 5 stations" with places the user
 * cannot see.
 *
 * A point whose models have not been fetched is **omitted and counted** in
 * `unknown` rather than folded in as an empty model list — "not loaded" is not
 * "not covered", and conflating them is what produced the bug above.
 */
export type CoverageStations = {
  stations: Forecast.CoverageStation[];
  /** Points whose model list has not been fetched yet. */
  unknown: number;
};

const getWeatherForecastModels = (state: CommonRootState) =>
  state.weather.forecastModels;

const getLocationForecastModels = (state: CommonRootState) =>
  state.location.forecastModels;

/** Server names, not catalogue keys — that is what a pin stores (see #177). */
const serverNames = (
  rows: Parameters<typeof Forecast.ForecastUtils.getForecastModelList>[0],
) => [
  ...new Set(
    Forecast.ForecastUtils.getForecastModelList(rows)
      .getList()
      .map((model) => model.serverId.forecastModel),
  ),
];

export const selectCoverageStations = createSelector(
  getSortedVisibleStationList,
  getWeatherForecastModels,
  getFavoriteLocations,
  getLocationForecastModels,
  (
    weatherStations,
    weatherModels,
    locations,
    locationModels,
  ): CoverageStations => {
    const stations: Forecast.CoverageStation[] = [];
    let unknown = 0;

    const add = (
      name: string,
      key: string | number,
      store: Record<string | number, unknown>,
    ) => {
      if (!(key in store)) {
        unknown += 1;
        return;
      }
      stations.push({
        name,
        models: serverNames(
          store[key] as Parameters<
            typeof Forecast.ForecastUtils.getForecastModelList
          >[0],
        ),
      });
    };

    weatherStations.forEach((station) =>
      add(Station.getStationName(station), station.id, weatherModels),
    );
    locations.forEach((location) =>
      add(location.title, location.hash, locationModels),
    );

    return {stations, unknown};
  },
  {memoizeOptions: {resultEqualityCheck: isEqual}},
);
